import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { CourseCodeEnum } from '@edtech-ui/constants/course-code.enum';
import { StudentsSubscriptionPlan } from '@edtech-ui/constants/current-subscription.enum';
import { SyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-generate-syllabus';
import { CurrentSubscription } from '@edtech-ui/core/models/student-courses-v2/student-products-lists';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { PaymentService } from '@edtech-ui/core/services/payment-management/payment.service';
import { StudentCoursesService } from '@edtech-ui/core/services/student-courses/student-courses.service';
import { EnrollCoursesDialogBoxComponent } from '@edtech-ui/student-dashboard-v2/enroll-courses-dialog-box/enroll-courses-dialog-box.component';
import { EnrollHardcodedMessageComponent } from '@edtech-ui/student-dashboard-v2/enroll-hardcoded-message/enroll-hardcoded-message.component';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-course-details',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule, MatTooltipModule,
		HasPermissionDirective
	],
	templateUrl: './course-details.component.html',
	styleUrl: './course-details.component.scss'
})
export class CourseDetailsComponent implements OnInit, OnDestroy {

	classId = environment.classroomId;
	isOpen = false;
	isLoading = false;
	courseId!: string;
	subscription = new Subscription();
	courseDetails: SyllabusResponseAI['Data'] | null = null;
	openIndex: number | null = null;
	PermissionForStudents = PermissionForStudents;
	courseCode!: string;
	isFreeTrial = false;
	currentSubscription!: CurrentSubscription;
	CourseCodeEnum = CourseCodeEnum;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private _studentCourseService: StudentCoursesService,
		private _courseService: CourseManagementService,
		private _toastr: ToastrService,
		private dialog: MatDialog,
		private _paymentService: PaymentService
	) { }

	ngOnInit(): void {
		this.getStudentProductData();
		this.route.paramMap.subscribe(params => {
			const courseId = params.get('id');
			if (courseId) {
				this.courseId = courseId;
				this.getCourseByCourseId();
			}
		});
	}

	postStudentEnroll() {

		if (this.courseCode !== CourseCodeEnum.COURSE_CODE && this.isFreeTrial) {
			this.openHardedCodeMessage();
			return;
		}

		this.subscription.add(
			this._studentCourseService.postStudentEnroll(this.courseId).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						this.router.navigate(['/student-dashboard/course-detail-page', this.classId, this.courseId]);
						this.getCourseByCourseId();
						return;
					}

					if (response.Status === 402 && response.Data) {

						const popupData = {
							title: response.Data.title,
							message: response.Data.message
						};

						const dialogRef = this.openEnrollCourseLimitDialogBox(popupData);
						this.router.events.subscribe(() => {
							dialogRef.close();
						});
						return;
					}
					this._toastr.error(response.Message);
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while enrolling student');
					return of(null);
				})
			).subscribe()
		)
	}

	getCourseByCourseId() {
		this.isLoading = true;
		this.subscription.add(
			this._courseService.getCoursesByCourseId(this.courseId).pipe(
				tap(response => {
					if (response.Success) {
						this.courseDetails = response.Data;
						this.courseCode = response.Data!.course_code;
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching courses details');
					return of(null);
				})
			).subscribe()
		)
	}

	goToCourseDescriptionPage(courseId: string) {
		this.router.navigate(['/student-dashboard/course-detail-page', this.classId, courseId])
	}

	goToProCourses() {
		this.router.navigate(['/student-dashboard/pro-course']);
	}

	goToMentorSession() {
		this.router.navigate(['/student-dashboard/mentors']);
	}

	toggleAccordion(index: number) {
		this.openIndex = this.openIndex === index ? null : index;
	}

	openEnrollCourseLimitDialogBox(data: { title: string, message: string }): MatDialogRef<EnrollCoursesDialogBoxComponent> {
		return this.dialog.open(EnrollCoursesDialogBoxComponent, {
			data
		});
	}

	getStudentProductData() {
		this.subscription.add(
			this._paymentService.getStudentProducts().pipe(
				tap(response => {
					if (response.Success) {
						this.currentSubscription = response.Data.current_subscription;
						this.isFreeTrial = this.currentSubscription?.product_name === StudentsSubscriptionPlan.FREE_TRIAL
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong .Please try again');
					return of(null);
				})
			).subscribe()
		);
	}

	openHardedCodeMessage() {
		this.dialog.open(EnrollHardcodedMessageComponent)
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}